import { Add, Edit, WarningAmber } from '@mui/icons-material';
import { Alert, Box, FormControl, Grid, InputLabel, Link, List, ListItem, MenuItem, Select, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MaterialReactTable from 'material-react-table';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import Spinner from 'modules/common/spinner.component';

import {
  HHG_WEIGHT_IMPORT_FIELDS,
  HHG_WEIGHT_IMPORT_WEIGHT_FIELDS,
  getHhgWeightImportColumnMappings,
  getHhgWeightImportFieldByFieldId,
  getInitialHhgWeightImportMappings,
  hhgWeightImportTemplateDisplayColumns,
} from './importHhgWeights.tableDefinitions';
import { ImportTemplateEditor } from './importTemplateEditor.component';
import { TabPanel, UploadTemplateFile, getImportErrorMessage } from './importAuthFiles.components';
import { authImportMappingTableProps, getPositionError, getPositionValue } from './importAuthFiles.tableDefinitions';

const useHhgWeightImportStyles = makeStyles(theme => ({
  sectionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
}));

const HHG_WEIGHT_TEMPLATE = {
  templateId: 0,
  templateTypeId: 4,
  name: '',
  startingRow: 2,
  mappings: [],
};

const HHG_WEIGHT_MAPPING = {
  hhgWeightMappingId: 0,
  templateId: 0,
  fieldId: 0,
  position: null,
};

const REQUIRED_TEMPLATE_NAME_ERROR = 'Template name is required';
const DATA_START_ROW_ERROR = 'Data start row is required and must be greater than zero';
const REQUIRED_MAPPING_ERROR = 'Position is required for this field';
const WEIGHT_MAPPING_ERROR = 'Map at least one actual weight column (Land, Air, or Sea).';
const FORM_ERROR_MESSAGE = 'Please fix the highlighted errors before saving.';

export const HHG_WEIGHT_IMPORT_TEMPLATE_TABS = {
  DRY_RUN_RESULTS: 'dryRunResults',
  IMPORT_RESULTS: 'importResults',
  MAPPING_DEFINITIONS: 'mappingDefinitions',
};

const weightFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

const formatWeight = value => (value === null || value === undefined ? '' : weightFormatter.format(value));

const hasValue = value => value !== null && value !== undefined && value !== '';

const createHhgWeightTemplate = () => ({
  ...HHG_WEIGHT_TEMPLATE,
  mappings: [],
});

const getPositiveNumberError = (value, message) => {
  if (!hasValue(value)) return message;

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? null : message;
};

const getPositiveNumberValue = value => {
  if (value === '') return '';

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : value;
};

const getTemplateFieldError = (field, value) => {
  switch (field) {
    case 'name':
      return value.trim() ? null : REQUIRED_TEMPLATE_NAME_ERROR;
    case 'startingRow':
      return getPositiveNumberError(value, DATA_START_ROW_ERROR);
    default:
      return null;
  }
};

const getTemplateErrors = template => ['name', 'startingRow'].reduce((errors, field) => {
  const error = getTemplateFieldError(field, template[field]);

  return error ? { ...errors, [field]: error } : errors;
}, {});

// Format problems the user already sees on a row, plus required fields left without a position
const getPositionErrors = mappingsByFieldKey => Object.values(mappingsByFieldKey).reduce((errors, mapping) => {
  const error = mapping.positionError || (!mapping.isWeight && !hasValue(mapping.position) ? REQUIRED_MAPPING_ERROR : null);

  return error ? { ...errors, [mapping.fieldKey]: error } : errors;
}, {});

const hasWeightMapping = mappingsByFieldKey => HHG_WEIGHT_IMPORT_WEIGHT_FIELDS.some(field => hasValue(mappingsByFieldKey[field.fieldKey]?.position));

const hasErrors = errors => Object.keys(errors).length > 0;

export const getHhgWeightImportTemplateId = template => template?.templateId || template?.id || template?.importTemplateId;

export const getHhgWeightImportTemplateName = template => template?.name ?? template?.templateName ?? '';

export const getHhgWeightImportTemplateList = response => {
  if (Array.isArray(response)) return response;

  return response?.templates ?? response?.data ?? [];
};

export const getHhgWeightImportResultErrors = importResults => importResults?.errors ?? importResults?.data?.errors ?? [];

const getHhgWeightImportResultEstimates = importResults => importResults?.estimates ?? importResults?.data?.estimates ?? [];

const getEstimateErrors = estimate => estimate?.errors ?? [];

// Estimates that could not be saved carry their own errors, while file-level problems stop the whole import
export const hasHhgWeightImportRowErrors = importResults => getHhgWeightImportResultEstimates(importResults)
  .some(estimate => getEstimateErrors(estimate).length > 0);

const getTemplateMappings = template => template?.mappings ?? [];

const getDisplayValue = value => (hasValue(value) ? String(value) : '');

const hydrateHhgWeightTemplate = template => {
  const freshTemplate = createHhgWeightTemplate();
  if (!template) return freshTemplate;

  return {
    ...freshTemplate,
    ...template,
    templateId: template.templateId ?? freshTemplate.templateId,
    templateTypeId: template.templateTypeId ?? freshTemplate.templateTypeId,
    name: getHhgWeightImportTemplateName(template),
    startingRow: template.startingRow ?? freshTemplate.startingRow,
    mappings: getTemplateMappings(template),
  };
};

const hydrateHhgWeightImportMappings = template => getTemplateMappings(template).reduce((mappingsByFieldKey, mapping) => {
  const field = getHhgWeightImportFieldByFieldId(mapping.fieldId);
  if (!field) return mappingsByFieldKey;

  return {
    ...mappingsByFieldKey,
    [field.fieldKey]: {
      ...field,
      ...mapping,
      positionError: null,
    },
  };
}, getInitialHhgWeightImportMappings());

// Weights left without a position are not part of the template
const buildHhgWeightTemplatePayload = (template, mappingsByFieldKey) => ({
  ...template,
  mappings: HHG_WEIGHT_IMPORT_FIELDS
    .map(field => mappingsByFieldKey[field.fieldKey])
    .filter(mapping => hasValue(mapping.position))
    .map(mapping => ({
      ...HHG_WEIGHT_MAPPING,
      hhgWeightMappingId: mapping.hhgWeightMappingId ?? HHG_WEIGHT_MAPPING.hhgWeightMappingId,
      templateId: mapping.templateId ?? template.templateId,
      fieldId: mapping.fieldId,
      position: mapping.position,
    })),
});

const getDisplayMappingRows = template => getTemplateMappings(template)
  .map(mapping => {
    const field = getHhgWeightImportFieldByFieldId(mapping.fieldId);

    return field ? { ...field, ...mapping, id: String(mapping.hhgWeightMappingId || mapping.fieldId) } : null;
  })
  .filter(Boolean)
  .sort((firstRow, secondRow) => Number(firstRow.position || 0) - Number(secondRow.position || 0));

export const HhgWeightTemplateSelector = props => {
  const { isLoading, onTemplateSelected, selectedTemplateId, templates } = props;

  return (
    <FormControl sx={{ minWidth: 350 }} size="small">
      <InputLabel id="hhg-weight-template-label">
        Select HHG Weights Import Template
      </InputLabel>
      <Select
        labelId="hhg-weight-template-label"
        id="hhg-weight-template-select"
        label="Select HHG Weights Import Template"
        disabled={isLoading}
        value={selectedTemplateId}
        onChange={event => onTemplateSelected(event.target.value)}
      >
        <MenuItem value="">Select a template</MenuItem>
        {templates.map(template => (
          <MenuItem key={getHhgWeightImportTemplateId(template)} value={String(getHhgWeightImportTemplateId(template))}>
            {getHhgWeightImportTemplateName(template)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

HhgWeightTemplateSelector.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onTemplateSelected: PropTypes.func.isRequired,
  selectedTemplateId: PropTypes.string.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export const HhgWeightTemplateEditorButton = props => {
  const { disabled, isEditMode, onClick } = props;
  const Icon = isEditMode ? Edit : Add;

  return (
    <Button variant="contained" color="primary" startIcon={<Icon />} onClick={onClick} disabled={disabled}>
      {isEditMode ? 'Edit Template' : 'Create HHG Weights Template'}
    </Button>
  );
};

HhgWeightTemplateEditorButton.defaultProps = {
  disabled: false,
};

HhgWeightTemplateEditorButton.propTypes = {
  disabled: PropTypes.bool,
  isEditMode: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

export const HhgWeightTemplateEditor = props => {
  const { initialTemplate, isEditMode, onClose, onSaveTemplate, open } = props;
  const classes = useHhgWeightImportStyles();
  const [template, setTemplate] = useState(createHhgWeightTemplate);
  const [templateErrors, setTemplateErrors] = useState({});
  const [mappingsByFieldKey, setMappingsByFieldKey] = useState(getInitialHhgWeightImportMappings);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const mappingRows = HHG_WEIGHT_IMPORT_FIELDS.map(field => mappingsByFieldKey[field.fieldKey]);
  const hasPositionErrors = mappingRows.some(mapping => Boolean(mapping.positionError));
  const showWeightMappingError = hasAttemptedSave && !hasWeightMapping(mappingsByFieldKey);
  const columns = useMemo(() => getHhgWeightImportColumnMappings({
    onPositionChange: (fieldKey, value) => setMappingsByFieldKey(currentMappings => ({
      ...currentMappings,
      [fieldKey]: {
        ...currentMappings[fieldKey],
        position: getPositionValue(value),
        positionError: getPositionError(value),
      },
    })),
  }), []);

  React.useEffect(() => {
    if (!open) return;

    setTemplate(isEditMode ? hydrateHhgWeightTemplate(initialTemplate) : createHhgWeightTemplate());
    setTemplateErrors({});
    setMappingsByFieldKey(isEditMode ? hydrateHhgWeightImportMappings(initialTemplate) : getInitialHhgWeightImportMappings());
    setHasAttemptedSave(false);
  }, [initialTemplate, isEditMode, open]);

  const updateTemplateValue = (field, value) => {
    setTemplate(currentTemplate => ({
      ...currentTemplate,
      [field]: value,
    }));

    setTemplateErrors(currentErrors => {
      if (!currentErrors[field]) return currentErrors;

      const error = getTemplateFieldError(field, value);
      const nextErrors = { ...currentErrors };

      if (error) {
        nextErrors[field] = error;
      } else {
        delete nextErrors[field];
      }

      return nextErrors;
    });
  };

  const handleClose = (...args) => {
    setTemplate(createHhgWeightTemplate());
    setTemplateErrors({});
    setMappingsByFieldKey(getInitialHhgWeightImportMappings());
    setHasAttemptedSave(false);
    onClose(...args);
  };

  const handleSave = async () => {
    const nextTemplateErrors = getTemplateErrors(template);
    const nextPositionErrors = getPositionErrors(mappingsByFieldKey);
    const isMissingWeight = !hasWeightMapping(mappingsByFieldKey);

    setTemplateErrors(nextTemplateErrors);
    setHasAttemptedSave(true);
    setMappingsByFieldKey(currentMappings => Object.entries(nextPositionErrors).reduce((nextMappings, [fieldKey, error]) => ({
      ...nextMappings,
      [fieldKey]: {
        ...nextMappings[fieldKey],
        positionError: error,
      },
    }), currentMappings));

    if (hasErrors(nextTemplateErrors) || hasErrors(nextPositionErrors) || isMissingWeight) return;

    const wasSaved = await onSaveTemplate(buildHhgWeightTemplatePayload(template, mappingsByFieldKey));

    if (wasSaved !== false) {
      handleClose();
    }
  };

  const getFormErrorMessage = () => {
    if (hasErrors(templateErrors) || hasPositionErrors) return FORM_ERROR_MESSAGE;
    if (showWeightMappingError) return WEIGHT_MAPPING_ERROR;
    return '';
  };

  const formErrorMessage = getFormErrorMessage();

  return (
    <ImportTemplateEditor
      open={open}
      onClose={handleClose}
      title={isEditMode ? 'Edit HHG Weights Import Template' : 'Create HHG Weights Import Template'}
      secondaryText="Define the template details and field mappings for an HHG actual weights import"
      actions={(
        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
          {formErrorMessage && (
            <Typography color="error" variant="body2">
              {formErrorMessage}
            </Typography>
          )}
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="primary" disabled={hasPositionErrors} onClick={handleSave}>
            {isEditMode ? 'Update Template' : 'Save Template'}
          </Button>
        </Stack>
      )}
    >
      <Box className={classes.sectionCard}>
        <Typography variant="h6" gutterBottom>
          Template Details
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Template Name"
              variant="outlined"
              size="small"
              required
              error={Boolean(templateErrors.name)}
              helperText={templateErrors.name || ''}
              value={template.name}
              onChange={event => updateTemplateValue('name', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Data Start Row"
              variant="outlined"
              size="small"
              required
              error={Boolean(templateErrors.startingRow)}
              helperText={templateErrors.startingRow || ''}
              value={template.startingRow}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              onChange={event => updateTemplateValue('startingRow', getPositiveNumberValue(event.target.value))}
            />
          </Grid>
        </Grid>
      </Box>

      <Box className={classes.sectionCard}>
        <Typography variant="h6" gutterBottom>
          Field Mappings
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={2}>
          File Number and HHG Estimate ID identify the estimate to update. Map at least one actual weight; actual weights without a column are not imported, and estimated weights are never changed.
        </Typography>
        {showWeightMappingError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {WEIGHT_MAPPING_ERROR}
          </Alert>
        )}
        <MaterialReactTable
          data={mappingRows}
          columns={columns}
          getRowId={row => row.fieldKey}
          {...authImportMappingTableProps}
        />
      </Box>
    </ImportTemplateEditor>
  );
};

HhgWeightTemplateEditor.propTypes = {
  initialTemplate: PropTypes.object,
  isEditMode: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveTemplate: PropTypes.func.isRequired,
};

HhgWeightTemplateEditor.defaultProps = {
  initialTemplate: null,
};

const HhgWeightFileNumberCell = props => {
  const { estimate, isSubmission } = props;
  const errors = getEstimateErrors(estimate);

  if (!isSubmission) return estimate.fileNumber;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Link href={`/authorizations/${estimate.fileId}`} target="_blank" rel="noopener noreferrer" underline="hover">
        {estimate.fileNumber}
      </Link>
      {errors.length > 0 && (
        <Tooltip title={errors.join(' ')}>
          <WarningAmber color="warning" fontSize="small" />
        </Tooltip>
      )}
    </Stack>
  );
};

HhgWeightFileNumberCell.propTypes = {
  estimate: PropTypes.shape({
    errors: PropTypes.arrayOf(PropTypes.string),
    fileId: PropTypes.number.isRequired,
    fileNumber: PropTypes.string.isRequired,
  }).isRequired,
  isSubmission: PropTypes.bool.isRequired,
};

const HhgWeightEstimateCell = props => {
  const { estimate, isSubmission } = props;
  const label = `${estimate.estimateName ?? ''} (ID: ${estimate.hhgEstimateId})`;

  if (!isSubmission) return label;

  // The HHG page opens on this estimate's segment and estimate
  const estimateUrl = `/authorizations/${estimate.fileId}/hhg?tab=ESTIMATE&segmentId=${estimate.hhgSegmentId}&estimateId=${estimate.hhgEstimateId}`;

  return (
    <Link href={estimateUrl} target="_blank" rel="noopener noreferrer" underline="hover">
      {label}
    </Link>
  );
};

HhgWeightEstimateCell.propTypes = {
  estimate: PropTypes.shape({
    estimateName: PropTypes.string,
    fileId: PropTypes.number.isRequired,
    hhgEstimateId: PropTypes.number.isRequired,
    hhgSegmentId: PropTypes.number.isRequired,
  }).isRequired,
  isSubmission: PropTypes.bool.isRequired,
};

// A weight column is only shown when the import supplies that weight for at least one estimate
const getHhgWeightImportResultColumns = (estimates, isSubmission) => [
  {
    accessorKey: 'fileNumber',
    header: 'File Number',
    Cell: ({ row }) => <HhgWeightFileNumberCell estimate={row.original} isSubmission={isSubmission} />,
  },
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'client', header: 'Client' },
  {
    accessorKey: 'estimateName',
    header: 'Estimate',
    Cell: ({ row }) => <HhgWeightEstimateCell estimate={row.original} isSubmission={isSubmission} />,
  },
  ...HHG_WEIGHT_IMPORT_WEIGHT_FIELDS
    .filter(field => estimates.some(estimate => hasValue(estimate[field.fieldKey])))
    .map(field => ({
      accessorKey: field.fieldKey,
      header: field.fieldName,
      Cell: ({ row }) => formatWeight(row.original[field.fieldKey]),
    })),
];

const HhgWeightImportResults = props => {
  const { emptyMessage, errorTitle, importResults, isSubmission, successMessage } = props;
  const estimates = useMemo(() => getHhgWeightImportResultEstimates(importResults).map((estimate, index) => ({
    ...estimate,
    id: `estimate-${estimate.hhgEstimateId ?? index}`,
  })), [importResults]);
  const columns = useMemo(() => getHhgWeightImportResultColumns(estimates, isSubmission), [estimates, isSubmission]);

  if (!importResults) {
    return (
      <Box pt={2}>
        <Typography variant="body2" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const errors = getHhgWeightImportResultErrors(importResults);
  if (errors.length > 0) {
    return (
      <Box pt={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorTitle} {errors.length} issue{errors.length === 1 ? '' : 's'}.
        </Alert>
        <List dense>
          {errors.map((error, index) => (
            <ListItem key={`${getImportErrorMessage(error)}-${index}`}>
              <Typography variant="body2">{getImportErrorMessage(error)}</Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  const authorizationCount = new Set(estimates.map(estimate => estimate.fileId)).size;
  const estimatesWithErrors = estimates.filter(estimate => getEstimateErrors(estimate).length > 0).length;

  return (
    <Box pt={2}>
      <Typography variant="body2" color="textSecondary" mb={2}>
        {successMessage}
      </Typography>
      {estimatesWithErrors > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {estimatesWithErrors} estimate{estimatesWithErrors === 1 ? ' was' : 's were'} not updated. Hover over the warning next to the file number to see why.
        </Alert>
      )}
      <Stack direction="row" spacing={4} mb={2}>
        <Box>
          <Typography variant="body2" color="textSecondary">Total Estimates</Typography>
          <Typography variant="h6">{estimates.length}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary">Total Authorizations</Typography>
          <Typography variant="h6">{authorizationCount}</Typography>
        </Box>
        {isSubmission && (
          <Box>
            <Typography variant="body2" color="textSecondary">Estimates With Errors</Typography>
            <Typography variant="h6">{estimatesWithErrors}</Typography>
          </Box>
        )}
      </Stack>
      <MaterialReactTable
        data={estimates}
        columns={columns}
        getRowId={row => row.id}
        enablePagination={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
      />
    </Box>
  );
};

HhgWeightImportResults.propTypes = {
  emptyMessage: PropTypes.string.isRequired,
  errorTitle: PropTypes.string.isRequired,
  importResults: PropTypes.object,
  isSubmission: PropTypes.bool,
  successMessage: PropTypes.string.isRequired,
};

HhgWeightImportResults.defaultProps = {
  importResults: null,
  isSubmission: false,
};

export const SelectedHhgWeightImportTemplate = props => {
  const {
    activeTab,
    dryRunResults,
    importResults,
    isDryRunLoading,
    isImportSubmitting,
    isLoading,
    onDryRun,
    onFileSelected,
    onSubmitImport,
    onTabChange,
    selectedTemplateFile,
    template,
  } = props;
  const classes = useHhgWeightImportStyles();

  if (isLoading) {
    return (
      <Box className={classes.sectionCard}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Spinner size="1x" />
          <Typography variant="body2">Loading HHG weights import template...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!template) return null;

  const mappingRows = getDisplayMappingRows(template);
  const hasDryRunErrors = getHhgWeightImportResultErrors(dryRunResults).length > 0;
  const canRunDryRun = Boolean(selectedTemplateFile && !isDryRunLoading && !isImportSubmitting);
  const canSubmitImport = Boolean(selectedTemplateFile && dryRunResults && !hasDryRunErrors && !isDryRunLoading && !isImportSubmitting);

  return (
    <>
      <Box className={classes.sectionCard}>
        <Grid container rowSpacing={1} columnSpacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
              <UploadTemplateFile
                key={getHhgWeightImportTemplateId(template)}
                clearTitle="Clear HHG Weights File"
                uploadLabel="Upload HHG Weights File"
                onFileSelected={onFileSelected}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={!canRunDryRun}
                onClick={onDryRun}
                startIcon={isDryRunLoading ? <Spinner size="1x" color="inherit" /> : null}
              >
                {isDryRunLoading ? 'Running...' : 'Dry Run'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!canSubmitImport}
                onClick={onSubmitImport}
                startIcon={isImportSubmitting ? <Spinner size="1x" color="inherit" /> : null}
              >
                {isImportSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">Template Name</Typography>
            <Typography variant="body1">{getHhgWeightImportTemplateName(template)}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">Data Start Row</Typography>
            <Typography variant="body1">{getDisplayValue(template.startingRow)}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">Selected File</Typography>
            <Typography variant="body1">{selectedTemplateFile?.name ?? ''}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.sectionCard}>
        <Tabs value={activeTab} onChange={(event, nextTab) => onTabChange(nextTab)} indicatorColor="primary" textColor="primary">
          <Tab value={HHG_WEIGHT_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS} label="Mapping Definitions" sx={{ px: 4 }} />
          <Tab value={HHG_WEIGHT_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS} label="Dry Run Results" disabled={!dryRunResults} sx={{ px: 4 }} />
          <Tab value={HHG_WEIGHT_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS} label="Import Results" disabled={!importResults} sx={{ px: 4 }} />
        </Tabs>
        <TabPanel currentTab={activeTab} value={HHG_WEIGHT_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS}>
          <Typography variant="h6" gutterBottom>
            Template Field Mappings
          </Typography>
          <MaterialReactTable
            data={mappingRows}
            columns={hhgWeightImportTemplateDisplayColumns}
            getRowId={row => row.id}
            {...authImportMappingTableProps}
          />
        </TabPanel>
        <TabPanel currentTab={activeTab} value={HHG_WEIGHT_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS}>
          <HhgWeightImportResults
            emptyMessage="Upload a file and run a dry run to preview the HHG weight updates."
            errorTitle="Dry run found"
            importResults={dryRunResults}
            successMessage="These estimates' actual weights will be updated when the import is submitted. Blank weights are left unchanged."
          />
        </TabPanel>
        <TabPanel currentTab={activeTab} value={HHG_WEIGHT_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS}>
          <HhgWeightImportResults
            emptyMessage="Submit a successfully validated file to view import results."
            errorTitle="Import found"
            importResults={importResults}
            isSubmission
            successMessage="These estimates' actual weights were updated by the import submission. Blank weights were left unchanged."
          />
        </TabPanel>
      </Box>
    </>
  );
};

SelectedHhgWeightImportTemplate.propTypes = {
  activeTab: PropTypes.string.isRequired,
  dryRunResults: PropTypes.object,
  importResults: PropTypes.object,
  isDryRunLoading: PropTypes.bool.isRequired,
  isImportSubmitting: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onDryRun: PropTypes.func.isRequired,
  onFileSelected: PropTypes.func.isRequired,
  onSubmitImport: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  selectedTemplateFile: PropTypes.object,
  template: PropTypes.object,
};

SelectedHhgWeightImportTemplate.defaultProps = {
  dryRunResults: null,
  importResults: null,
  selectedTemplateFile: null,
  template: null,
};
